import {numberInputAnatomy as parts} from '@chakra-ui/anatomy';
import {createMultiStyleConfigHelpers, defineStyle} from '@chakra-ui/styled-system';
import {cssVar} from '@chakra-ui/theme-tools';
import {runIfFn} from '@chakra-ui/utils';

import {$borderRadius as $inputBorderRadius, $padding as $inputPadding, inputTheme} from './Input';

const {defineMultiStyleConfig, definePartsStyle} = createMultiStyleConfigHelpers(parts.keys);

const $stepperWidth = cssVar(`number-input-stepper-width`);
const $numberInputPadding = cssVar(`number-input-input-padding`);

const $bg = cssVar(`number-input-bg`);
const $fg = cssVar(`number-input-color`);
const $border = cssVar(`number-input-border-color`);

const baseStyleRoot = defineStyle({
  [$stepperWidth.variable]: `calc(1rem + ${$inputPadding.reference})`,
  [$numberInputPadding.variable]: $stepperWidth.reference,
});

const baseStyleField = defineStyle(props => ({
  ...runIfFn(inputTheme.baseStyle, props)?.field,
  // you can pass text-align to parent (NumberInput)
  // and input will be aligned accordingly
  textAlign: `inherit`,
}));

const baseStyleStepperGroup = defineStyle({
  width: $stepperWidth.reference,
});

const baseStyleStepper = defineStyle({
  borderStart: `none`,
  color: $fg.reference,
  bg: $bg.reference,
  [$fg.variable]: `colors.chakra-body-text`,
  [$border.variable]: `colors.chakra-border-color`,
  _dark: {
    [$fg.variable]: `colors.whiteAlpha.800`,
    [$border.variable]: `colors.whiteAlpha.300`,
  },
  _active: {
    [$bg.variable]: `colors.gray.200`,
    _dark: {
      [$bg.variable]: `colors.whiteAlpha.300`,
    },
  },
  _disabled: {
    opacity: 0.4,
    cursor: `not-allowed`,
  },
});

const baseStyle = definePartsStyle(props => ({
  root: baseStyleRoot,
  field: baseStyleField(props),
  stepperGroup: baseStyleStepperGroup,
  stepper: baseStyleStepper,
}));

const getSize = (size: 'md' | 'lg') => {
  const sizeStyle = inputTheme.sizes![size];

  return definePartsStyle({
    root: {
      [$inputPadding.variable]: sizeStyle.field[$inputPadding.variable],
      [$inputBorderRadius.variable]: sizeStyle.field[$inputBorderRadius.variable],
    },
    field: {
      ...sizeStyle.field,
      paddingInlineEnd: $numberInputPadding.reference,
      verticalAlign: `top`,
    },
    stepper: {
      _first: {
        borderTopEndRadius: $inputBorderRadius.reference,
      },
      _last: {
        borderBottomEndRadius: $inputBorderRadius.reference,
        mt: `0`,
        borderTopWidth: 0,
      },
    },
  });
};

const sizes = {
  md: getSize(`md`),
  lg: getSize(`lg`),
};

export const numberInputTheme = defineMultiStyleConfig({
  baseStyle,
  sizes,
  variants: inputTheme.variants,
  defaultProps: {
    ...inputTheme.defaultProps,
    size: `md`,
  },
});
